import { AgeGenderStandalone } from '@/components/age-and-gender';
import { StandaloneParentalConsent } from '@/components/age-and-gender/standalone-parental-consent';
import consentStepMessages from '@/components/registration/consent-step/messages';
import commonMessages from '@/lang/messages/common';
import { accountToAgeGenderAnalytics } from '@/libs/analytics/transformers';
import {
    DialogScreenOverrideProvider,
    useDialogScreenOverride,
} from '@/providers/dialog-screen-override';
import { styleInfoColor } from '@/utils/styles';
import { EventProperties } from '@calm/evexia-analytics';
import { DS_HEIGHT } from '@calm/evexia-design-tokens';
import { Check } from '@mui/icons-material';
import useAccount from '@rhg/hooks/use-account';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useGetCircles } from '@rhg/hooks/use-circles';
import {
    getConsentContext,
    useConsentContext,
} from '@rhg/hooks/use-consent-context';
import { useConsents } from '@rhg/hooks/use-consents';
import useHasAcceptedCohortTos from '@rhg/hooks/use-has-accepted-tos';
import { useGetMostRecentSponsorship } from '@rhg/hooks/use-most-recent-sponsorship';
import useToastDialog from '@rhg/hooks/use-toast-dialog';
import { useGetUnsponsoredConfig } from '@rhg/hooks/use-unsponsored-config';
import profileMessages from '@rhg/lang/messages/profile';
import { logger } from '@rhg/libs/logger';
import { Box, Dialog, Typography } from '@rhg/libs/material';
import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

const styleIconContainer = {
    height: DS_HEIGHT.AVATAR_MEDIUM,
    width: DS_HEIGHT.AVATAR_MEDIUM,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const styleConfirmContainer = {
    color: styleInfoColor,
    backgroundColor: 'info.light',
    ...styleIconContainer,
};

export enum AgeGenderDialogStep {
    AgeGender = 'AgeGenderStep',
    ParentalConsent = 'ParentalConsentStep',
}

interface AgeGenderDialogProps {
    isOpen: boolean;
    onClose: () => void;
    currentStep?: AgeGenderDialogStep;
}

function AgeGenderDialogContent({
    isOpen,
    onClose,
    currentStep = AgeGenderDialogStep.AgeGender,
}: AgeGenderDialogProps) {
    const id = useId();
    const { trackDialogPageNav, trackDialogClose } = useDialogScreenOverride();
    const { formatMessage } = useIntl();
    const { toast } = useToastDialog();
    const { activeCircle } = useActiveCircle();
    const { refetch: refetchConsent } = useHasAcceptedCohortTos();
    const { getCirclesAsync } = useGetCircles();
    const { refetch: refetchAccount, account } = useAccount();
    /** Needed to ensure we don't log more page navs when dialogStep === previousStep   */
    const previousStep = useRef<AgeGenderDialogStep | undefined>(undefined);

    const trackProps = useMemo<EventProperties>(
        () => ({
            Source: 'Profile',
            ...(account ? accountToAgeGenderAnalytics(account) : {}),
        }),
        [account]
    );

    const { getMostRecentSponsorshipAsync } = useGetMostRecentSponsorship();
    const { getUnsponsoredConfigAsync } = useGetUnsponsoredConfig();

    const [dialogStep, _setDialogStep] =
        useState<AgeGenderDialogStep>(currentStep);

    const setDialogStep = useCallback(
        (step: AgeGenderDialogStep) => {
            previousStep.current = dialogStep;
            _setDialogStep(step);
        },
        [dialogStep]
    );

    const { consents } = useConsents();

    const handleClose = useCallback(() => {
        trackDialogClose();
        onClose();
    }, [trackDialogClose, onClose]);

    const finishUpdate = useCallback(() => {
        toast({
            title: formatMessage(
                profileMessages['profile.about_you.profile_updated']
            ),
            icon: (
                <Box sx={styleConfirmContainer}>
                    <Check color="inherit" fontSize="large" />
                </Box>
            ),
        });
        handleClose();
    }, [formatMessage, toast, handleClose]);

    const handleProfileUpdate = useCallback(async () => {
        const consentData = await refetchConsent();
        // Force refresh circles and sponsorships
        await getCirclesAsync(true);
        const consentConfig = activeCircle?.sponsor
            ? await getMostRecentSponsorshipAsync(activeCircle)
            : await getUnsponsoredConfigAsync();
        const consents = consentConfig?.consents;
        const updatedConsentRequirements = getConsentContext({
            accountConsents: consentData.data?.consentData
                ? [consentData.data.consentData]
                : [],
            accountUserConsents: consentData.data?.userResponse
                ? [consentData.data.userResponse]
                : [],
            sponsorConsents: consents,
        });

        await refetchAccount();

        if (updatedConsentRequirements.parental.isRequired) {
            setDialogStep(AgeGenderDialogStep.ParentalConsent);
            return;
        }

        finishUpdate();
    }, [
        activeCircle,
        finishUpdate,
        getCirclesAsync,
        getMostRecentSponsorshipAsync,
        getUnsponsoredConfigAsync,
        refetchAccount,
        refetchConsent,
        setDialogStep,
    ]);

    const { consent } = useHasAcceptedCohortTos(undefined);
    const accountConsents = useMemo(
        () => (consent?.consentData ? [consent.consentData] : []),
        [consent?.consentData]
    );
    const accountUserConsents = consent?.userResponse
        ? [consent.userResponse]
        : [];

    const { consentRequirements } = useConsentContext({
        accountConsents,
        accountUserConsents,
        sponsorConsents: consents,
    });

    const srTitle = formatMessage(
        dialogStep === AgeGenderDialogStep.ParentalConsent
            ? consentStepMessages.parentalConsentHeading
            : profileMessages['profile.about_you.dobAndGender_lower_case']
    );

    useEffect(() => {
        if (!isOpen || dialogStep === previousStep.current) {
            return;
        }
        if (dialogStep === AgeGenderDialogStep.ParentalConsent) {
            trackDialogPageNav('Consent', 'Profile', undefined, trackProps);
        } else {
            trackDialogPageNav(
                'AgeAndGender',
                'Profile',
                undefined,
                trackProps
            );
        }
    }, [dialogStep, isOpen, trackDialogPageNav, trackProps]);

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            fullScreen
            trackDialog="NotMapped"
            trackProps={trackProps}
            aria-labelledby={id}
        >
            {dialogStep === AgeGenderDialogStep.ParentalConsent ? (
                <StandaloneParentalConsent
                    consentRequirements={consentRequirements}
                    accountConsents={accountConsents}
                    onBack={() => setDialogStep(AgeGenderDialogStep.AgeGender)}
                    onClose={finishUpdate}
                    trackProps={trackProps}
                />
            ) : (
                <AgeGenderStandalone
                    onCancel={handleClose}
                    // TODO(EVE-10506): failure logs to Datadog only; needs user-facing error UX
                    onConfirm={() => {
                        handleProfileUpdate().catch((err) =>
                            logger.error(
                                'Failed to update age and gender profile',
                                {},
                                err as Error
                            )
                        );
                    }}
                    primaryCtaLabel={formatMessage(
                        commonMessages['common.save']
                    )}
                    primaryCtaStartIcon={<Check />}
                    showAddEditButtons
                    sx={{ px: { xs: 2, md: 0 } }}
                    title={formatMessage(
                        profileMessages[
                            'profile.about_you.dob_and_gender_dialog_title'
                        ]
                    )}
                    heading={formatMessage(
                        profileMessages[
                            'profile.about_you.dobAndGender_lower_case'
                        ]
                    )}
                    trackProps={trackProps}
                    trackScreen="AgeAndGender"
                />
            )}
            <Typography variant="srOnly" id={id}>
                {srTitle}
            </Typography>
        </Dialog>
    );
}

export function AgeGenderDialog(props: AgeGenderDialogProps) {
    return (
        <DialogScreenOverrideProvider>
            <AgeGenderDialogContent {...props} />
        </DialogScreenOverrideProvider>
    );
}
